// src/pages/ReservationPage.js
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import emailjs from 'emailjs-com';
import Footer from '../components/Footer';
import '../styles/ReservationPage.css';
import heroImg from '../assets/P3.jpg';

const EMAILJS_SERVICE  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_USER     = process.env.REACT_APP_EMAILJS_USER_ID;
const TEMPLATE_RESTAURANT = process.env.REACT_APP_EMAILJS_RESERVATION_RESTAURANT;
const TEMPLATE_GUEST      = process.env.REACT_APP_EMAILJS_RESERVATION_GUEST;
const SHEETS_WEBHOOK_URL  = process.env.REACT_APP_SHEETS_WEBHOOK_URL;

// Time slots available for booking
const TIME_SLOTS = [
  '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM',
  '8:30 PM', '9:00 PM', '9:30 PM', '10:00 PM',
];

const PARTY_SIZES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '10+'];

const ReservationPage = () => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const [status, setStatus] = useState(null); // 'sending' | 'success' | 'error'

  // Get today's date in YYYY-MM-DD for the min date attribute
  const today = new Date().toISOString().split('T')[0];

  const onSubmit = async (data) => {
    setStatus('sending');

    const templateParams = {
      guest_name:      data.name,
      guest_email:     data.email,
      guest_phone:     data.phone,
      date:            data.date,
      time:            data.time,
      party_size:      data.partySize,
      special_requests: data.specialRequests || 'None',
    };

    try {
      // Send notification to restaurant
      await emailjs.send(
        EMAILJS_SERVICE,
        TEMPLATE_RESTAURANT,
        templateParams,
        EMAILJS_USER
      );

      // Send confirmation to guest
      await emailjs.send(
        EMAILJS_SERVICE,
        TEMPLATE_GUEST,
        templateParams,
        EMAILJS_USER
      );

      setStatus('success');
      reset();

      // Best-effort log to the restaurant's Google Sheet — this runs after the
      // emails already succeeded and is intentionally not awaited, so a
      // failure here (e.g. the Apps Script endpoint being unreachable) never
      // blocks the guest-facing confirmation, which is the reliable part.
      if (SHEETS_WEBHOOK_URL) {
        fetch(SHEETS_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(templateParams),
        }).catch((sheetsErr) => {
          console.error('Google Sheets logging failed (non-critical):', sheetsErr);
        });
      }
    } catch (err) {
      console.error('=== EmailJS Error ===');
      console.error('Status:', err.status);
      console.error('Text:', err.text);
      console.error('Service:', EMAILJS_SERVICE);
      console.error('Restaurant template:', TEMPLATE_RESTAURANT);
      console.error('Guest template:', TEMPLATE_GUEST);
      setStatus('error');
    }
  };

  return (
    <div className="reservation-page">
      <div className="reservation-layout">

        {/* ── Left: info panel ── */}
        <div className="reservation-info">
          <div>
            <span className="reservation-info__eyebrow">Book a Table</span>
            <h1 className="reservation-info__title">Reserve Your Spot at Lebua Thai</h1>
            <p className="reservation-info__sub">
              Fill in the form and we will confirm your reservation by email or WhatsApp.
              For large groups or corporate events, feel free to reach us directly.
            </p>
          </div>

          <img
            src={heroImg}
            alt="Lebua Thai restaurant interior"
            className="reservation-info__img"
            loading="lazy"
          />

          <div className="reservation-details">
            <div className="reservation-detail">
              <span className="reservation-detail__icon" aria-hidden="true">📍</span>
              <div className="reservation-detail__text">
                <strong>Location</strong>
                30 Victoria Street, #01-31 CHIJMES<br />Singapore 187996
              </div>
            </div>
            <div className="reservation-detail">
              <span className="reservation-detail__icon" aria-hidden="true">🕐</span>
              <div className="reservation-detail__text">
                <strong>Opening Hours</strong>
                Monday – Sunday: 11:30 AM – 3:00 PM &amp; 5:30 PM – 11:00 PM<br />Last Orders: 10:00 PM
              </div>
            </div>
            <div className="reservation-detail">
              <span className="reservation-detail__icon" aria-hidden="true">📞</span>
              <div className="reservation-detail__text">
                <strong>Phone / WhatsApp</strong>
                <a href="tel:+6590079953" style={{ color: 'var(--color-gold)' }}>+65 9007 9953</a>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: form panel ── */}
        <div className="reservation-form-wrap">
          <h2>Reservation Details</h2>
          <p>We'll send a confirmation to your email once we receive your request.</p>

          <form className="res-form" onSubmit={handleSubmit(onSubmit)} noValidate>

            {/* Name */}
            <div className="form-field">
              <label htmlFor="res-name">Full Name *</label>
              <input
                id="res-name"
                type="text"
                placeholder="Your name"
                className={errors.name ? 'field-error' : ''}
                {...register('name', { required: 'Please enter your name' })}
              />
              {errors.name && <span className="field-error-msg">{errors.name.message}</span>}
            </div>

            {/* Email + Phone */}
            <div className="res-row">
              <div className="form-field">
                <label htmlFor="res-email">Email *</label>
                <input
                  id="res-email"
                  type="email"
                  placeholder="you@email.com"
                  className={errors.email ? 'field-error' : ''}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' }
                  })}
                />
                {errors.email && <span className="field-error-msg">{errors.email.message}</span>}
              </div>
              <div className="form-field">
                <label htmlFor="res-phone">Phone *</label>
                <input
                  id="res-phone"
                  type="tel"
                  placeholder="+65 9000 0000"
                  className={errors.phone ? 'field-error' : ''}
                  {...register('phone', { required: 'Phone number is required' })}
                />
                {errors.phone && <span className="field-error-msg">{errors.phone.message}</span>}
              </div>
            </div>

            {/* Date + Time */}
            <div className="res-row">
              <div className="form-field">
                <label htmlFor="res-date">Date *</label>
                <input
                  id="res-date"
                  type="date"
                  min={today}
                  className={errors.date ? 'field-error' : ''}
                  {...register('date', { required: 'Please select a date' })}
                />
                {errors.date && <span className="field-error-msg">{errors.date.message}</span>}
              </div>
              <div className="form-field">
                <label htmlFor="res-time">Time *</label>
                <select
                  id="res-time"
                  className={errors.time ? 'field-error' : ''}
                  {...register('time', { required: 'Please select a time' })}
                >
                  <option value="">Select time</option>
                  {TIME_SLOTS.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {errors.time && <span className="field-error-msg">{errors.time.message}</span>}
              </div>
            </div>

            {/* Party size */}
            <div className="form-field">
              <label htmlFor="res-party">Number of Guests *</label>
              <select
                id="res-party"
                className={errors.partySize ? 'field-error' : ''}
                {...register('partySize', { required: 'Please select party size' })}
              >
                <option value="">Select guests</option>
                {PARTY_SIZES.map(n => (
                  <option key={n} value={n}>{n} {n === '1' ? 'guest' : 'guests'}</option>
                ))}
              </select>
              {errors.partySize && <span className="field-error-msg">{errors.partySize.message}</span>}
            </div>

            {/* Special requests */}
            <div className="form-field">
              <label htmlFor="res-requests">Special Requests</label>
              <textarea
                id="res-requests"
                placeholder="Dietary requirements, celebrations, seating preference..."
                {...register('specialRequests')}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary res-submit"
              disabled={status === 'sending'}
            >
              {status === 'sending' ? 'Sending…' : 'Request Reservation'}
            </button>

          </form>

          {status === 'success' && (
            <div className="res-status res-status--success">
              ✓ Reservation request sent! Check your email for confirmation. We will be in touch shortly.
            </div>
          )}
          {status === 'error' && (
            <div className="res-status res-status--error">
              Something went wrong. Please try again or call us at <a href="tel:+6590079953" style={{ color: 'inherit', textDecoration: 'underline' }}>+65 9007 9953</a>.
            </div>
          )}
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default ReservationPage;
